import * as THREE from 'three';

// Where the lobby's doorway is, and the orientation that puts it in the middle of the frame.
// Kept out of three.js's scene graph, in the way of the other Utils modules, so the maths can
// be tested under jsdom.
//
// `openDoors` used to aim the camera by slerping to the `doorTarget` mesh's own quaternion, and
// that mesh is placed with `position.copy` and never given a quaternion — so it is the identity,
// and slerping to it aims the camera down the world's -Z. That is the doorway only while the
// camera is still parked on the axis it arrived on. The walk lets it stand two units off that
// axis and a unit and a half outside the doors, and the doorway is 53° off the -Z bearing from
// there: the turn finishes with the doors out at the edge of the frame.
//
// The doorway is measured from the leaves instead. Nothing here is pooled: a door is opened once
// per page load.

/** The two leaves the doorway is made of, by the names they carry inside the gate's GLB. */
export const DOOR_LEAF_NAMES = ['Door_left', 'Door_right'];

function isFinitePoint(point) {
    return Number.isFinite(point.x) && Number.isFinite(point.y) && Number.isFinite(point.z);
}

/**
 * The centre of the doorway, from the leaves that close it.
 *
 * The middle of the span the two closed leaves cover — which is the doorway — and not the middle
 * of their origins. Nothing outside the GLB can tell where a leaf's own origin sits: at its
 * hinge, at its own centre, or wherever the modeller put it. Those conventions disagree as soon
 * as the two leaves are not the same width, and only the span agrees with all of them.
 *
 * @param {THREE.Object3D[]} leaves - the door leaves, as children of the gate.
 * @returns {THREE.Vector3|null} the centre in world space, or `null` when there is nothing to
 *   measure — and when what there is measures to something that is not a place. A leaf left with
 *   a NaN transform builds a box that is *not* empty, because every comparison against NaN is
 *   false, and a NaN centre carries straight into the camera's own matrix.
 */
export function doorwayCentre(leaves) {
    if (!leaves) return null;

    const box = new THREE.Box3();

    leaves.forEach((leaf) => {
        if (leaf && leaf.isObject3D) box.expandByObject(leaf);
    });

    if (box.isEmpty()) return null;

    const centre = box.getCenter(new THREE.Vector3());

    return isFinitePoint(centre) ? centre : null;
}

/**
 * The orientation that puts `centre` in the middle of the frame from `cameraPosition`.
 *
 * `Matrix4.lookAt` is the camera-style form of look-at: it builds the rotation whose -Z runs from
 * the eye towards the target, with the up it is given. That is the orientation a camera wants, and
 * the one to slerp to. `Object3D.lookAt` is the other form — it aims an object's +Z at the target
 * and takes a camera's -Z as a special case — so it cannot be used to build a rotation in the
 * abstract, which is what the aim needs before it is slerped into the camera's.
 *
 * The answer is a world orientation, and that is what the camera wants: the rig the camera hangs
 * from is never turned (Initial3DSetup puts it at the origin and nothing rotates it), so the
 * camera's own quaternion is a world one — the same assumption `LobbyWalkControls.updateLook`
 * already makes when it writes the camera's orientation from a world-space yaw and pitch.
 *
 * @param {THREE.Vector3} cameraPosition - where the camera is standing.
 * @param {THREE.Vector3|null} centre - the point to put in the middle of the frame.
 * @param {THREE.Vector3} [up] - which way is up for the camera; its own up by default.
 * @returns {THREE.Quaternion|null} the orientation, or `null` when there is no direction to take:
 *   nothing to aim at, a centre that is not a place, or the camera standing on the aim point
 *   itself. three's own lookAt answers the identity for that last case rather than saying it
 *   cannot, and an identity aim is the defect this module exists to remove.
 */
export function doorAimQuaternion(cameraPosition, centre, up = THREE.Object3D.DEFAULT_UP) {
    if (!centre || !isFinitePoint(centre)) return null;

    const direction = new THREE.Vector3().subVectors(centre, cameraPosition);

    if (direction.lengthSq() === 0) return null;

    const matrix = new THREE.Matrix4().lookAt(cameraPosition, centre, up);

    return new THREE.Quaternion().setFromRotationMatrix(matrix);
}
